import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { VimNotificationsService } from '@skyeng/libs/vimbox/notifications';
import { ISkyApiRequestOptions, SkyApiService } from '@skyeng/libs/shared/api';
import { Store } from '@skyeng/libs/shared/store';
import { IPermissionItem, IPermissionsData, IPermissionsResponse } from '../models/interfaces';
import { NOTIFICATION_COLORS } from '../configs';
import { MENU_LIST } from '../models/configs';
import { TranslocoService } from '@ngneat/transloco';
import { environment } from '@environment';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { UserService } from '@shared/core';

const api = {
  permissions: '/api/config/permissions',
};

const storeInitialState: IPermissionsData = {
  permissions: [],
  currentCanModify: false,
  menuList: MENU_LIST,
};

@Injectable({
  providedIn: 'root',
})
export class OperatorForecastPermissionsService {
  private endpoints = [environment.wfm.host];
  private baseRequestParams: ISkyApiRequestOptions = {
    withCredentials: true,
  };
  private store = new Store<IPermissionsData>(storeInitialState);
  permissions$ = this.store.select('permissions');
  menuList$ = this.store.select('menuList');
  canModify$ = this.store.select('currentCanModify');

  constructor(
    private skyApiService: SkyApiService,
    private vimNotificationsService: VimNotificationsService,
    private translocoService: TranslocoService,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.subscribeToModifyPermission();
  }

  getPermissionList(): Observable<IPermissionItem[]> {
    return this.getPermissions().pipe(
      catchError((error: HttpErrorResponse) => {
        const errorMessage = error.error?.message || error.message;
        this.vimNotificationsService.add({
          text: this.translocoService.translate('wfmModule.errors.processRequest', {
            entity: this.translocoService.translate('wfmModule.entities.ofAccess', {
              item: this.translocoService.translate('wfmModule.entities.fewList'),
            }),
            errorMessage: errorMessage,
          }),
          styles: NOTIFICATION_COLORS.error,
        });
        return of({ items: [] as IPermissionItem[] });
        // return of(PERMISSION_LIST.items);
      }),
      map(list => list.items),
    );
  }

  updatePermissions(permissions: IPermissionItem[]): void {
    this.updateState({ permissions });
    this.updateMenuList();
  }

  updateModifyPermission(code: string | null): void {
    this.updateState({ currentCanModify: this.hasPermission(code) });
  }

  hasPermission(code?: string | null): boolean {
    if (!code) {
      return false;
    }
    const permissions = this.store.get('permissions');
    const user = this.userService.getUser();
    const permission = permissions.find(item => item.code === code);

    return !!permission && permission?.roles.some(item => user.roles.includes(item));
  }

  getCanModify(): boolean {
    return this.store.get('currentCanModify');
  }

  private getPermissions(): Observable<IPermissionsResponse> {
    return this.skyApiService.get<IPermissionsResponse>(this.endpoints, api.permissions, {
      ...this.baseRequestParams,
    });
  }

  private updateMenuList(): void {
    this.updateState({
      menuList: MENU_LIST.map(element => {
        if (!element.items) {
          return { ...element, isVisible: this.hasPermission(element.permissionCode) };
        }
        return {
          ...element,
          items: element.items.map(item => ({ ...item, isVisible: this.hasPermission(item.permissionCode) })),
          isVisible: element.items.some(item => this.hasPermission(item.permissionCode)),
        };
      }),
    });
  }

  private updateState(values: Partial<IPermissionsData>): void {
    this.store.updateFields(values);
  }

  private subscribeToModifyPermission(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.route.snapshot),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
      )
      .subscribe((route: ActivatedRouteSnapshot) => {
        this.updateModifyPermission(route.data?.permissions?.edit);
      });
  }
}
